if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {};
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mediaAbortController = null;
        this.bpdPreloadIdleCallback = null;
        this.bpdProgressAnimationFrame = null;
        this.bpdTouchSwipe = null;
        this.bpdSuppressClick = false;
        this.bpdSuppressClickTimeout = null;
        this.bpdMobileDotsStartIndex = 0;
        this.bpdRestoreSyncAnimationFrame = null;
        this.bpdRestoreSyncTimeouts = [];
        this.bpdPendingScrollMediaId = null;
        this.bpdPendingScrollTargetLeft = 0;
        this.bpdPendingScrollUntil = 0;
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.onBpdThumbnailClick = this.onBpdThumbnailClick.bind(this);
        this.onBpdArrowClick = this.onBpdArrowClick.bind(this);
        this.onBpdMediaQueryChange = this.onBpdMediaQueryChange.bind(this);
        this.syncBpdMobileStateFromScroll = this.syncBpdMobileStateFromScroll.bind(this);
        this.scheduleBpdRestoredScrollSync = this.scheduleBpdRestoredScrollSync.bind(this);
        this.onBpdGalleryScroll = this.onBpdGalleryScroll.bind(this);
        this.onBpdPointerDown = this.onBpdPointerDown.bind(this);
        this.onBpdPointerMove = this.onBpdPointerMove.bind(this);
        this.onBpdPointerUp = this.onBpdPointerUp.bind(this);
        this.onBpdPointerCancel = this.onBpdPointerCancel.bind(this);
        this.onBpdViewerClick = this.onBpdViewerClick.bind(this);
        this.debouncedOnSlideChanged = debounce(this.onSlideChanged, 500);
        this.debouncedPreloadBpdGalleryMedia = debounce(() => this.preloadBpdGalleryMedia(), 100);

        this.refreshMedia();
        this.mql.addEventListener('change', this.onBpdMediaQueryChange);
        window.addEventListener('load', this.scheduleBpdRestoredScrollSync);
        window.addEventListener('pageshow', this.scheduleBpdRestoredScrollSync);
      }

      disconnectedCallback() {
        this.mediaAbortController?.abort();
        this.mql.removeEventListener('change', this.onBpdMediaQueryChange);
        window.removeEventListener('load', this.scheduleBpdRestoredScrollSync);
        window.removeEventListener('pageshow', this.scheduleBpdRestoredScrollSync);
        this.cancelBpdNextMediaWarmup();
        if (this.bpdProgressAnimationFrame) window.cancelAnimationFrame(this.bpdProgressAnimationFrame);
        if (this.bpdSuppressClickTimeout) window.clearTimeout(this.bpdSuppressClickTimeout);
        if (this.bpdRestoreSyncAnimationFrame) window.cancelAnimationFrame(this.bpdRestoreSyncAnimationFrame);
        this.bpdRestoreSyncTimeouts.forEach((timeout) => window.clearTimeout(timeout));
        this.clearBpdPendingProgrammaticScroll();
      }

      refreshMedia() {
        this.mediaAbortController?.abort();
        this.mediaAbortController = new AbortController();
        const signal = this.mediaAbortController.signal;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          progress: this.querySelector('.bpd-gallery-progress'),
          progressBar: this.querySelector('.bpd-gallery-progress__bar'),
          arrowControls: this.querySelector('.bpd-gallery-arrows'),
        };

        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', this.debouncedOnSlideChanged, { signal });
        this.elements.viewer.slider?.addEventListener('scroll', this.onBpdGalleryScroll, { passive: true, signal });
        this.elements.thumbnails?.addEventListener('click', this.onBpdThumbnailClick, { signal });
        this.elements.arrowControls?.addEventListener('click', this.onBpdArrowClick, { capture: true, signal });

        if (window.PointerEvent && this.isBpdTouchCapable()) {
          this.elements.viewer.addEventListener('pointerdown', this.onBpdPointerDown, { signal });
          this.elements.viewer.addEventListener('pointermove', this.onBpdPointerMove, { passive: false, signal });
          this.elements.viewer.addEventListener('pointerup', this.onBpdPointerUp, { signal });
          this.elements.viewer.addEventListener('pointercancel', this.onBpdPointerCancel, { signal });
          this.elements.viewer.addEventListener('click', this.onBpdViewerClick, { capture: true, signal });
        }

        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        this.sortInitialColorMedia();
        this.preloadBpdGalleryMedia();
        this.updateBpdProgress();
        this.updateBpdArrowState();
        this.updateBpdActiveMediaRatio();
        this.updateBpdMobileDots();
        this.scheduleBpdRestoredScrollSync();
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onBpdThumbnailClick(event) {
        const target = event.target.closest('[data-target]');
        if (!target || !this.elements.thumbnails?.contains(target)) return;

        this.setActiveMedia(target.dataset.target, false);
      }

      onBpdMediaQueryChange() {
        this.updateBpdMobileDots();
        this.updateBpdActiveMediaRatio();
        this.preloadBpdGalleryMedia();
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        const thumbnail = mediaId ? this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`) : null;
        this.setActiveThumbnail(thumbnail);
        this.preloadBpdGalleryMedia(event.detail.currentElement);
        this.updateBpdProgress(event.detail.currentElement);
        this.updateBpdArrowState();
        this.updateBpdActiveMediaRatio(event.detail.currentElement);
        this.updateBpdMobileDots(event.detail.currentElement);
      }

      setActiveColorMedia(mediaId, shouldScroll = true) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const mediaColor = activeMedia?.dataset.bpdMediaColor;

        if (!mediaColor) {
          this.setActiveMedia(mediaId, true, shouldScroll);
          return;
        }

        if (this.dataset.bpdSelectedMediaColor === mediaColor) {
          this.applyColorScope(mediaColor);
          this.updateBpdProgress();
          this.updateBpdArrowState();
          this.updateBpdActiveMediaRatio();
          this.updateBpdMobileDots();
          this.preloadBpdGalleryMedia();
          return;
        }

        this.sortMediaByColor(mediaColor);
        this.setActiveMedia(mediaId, false, shouldScroll);
      }

      sortInitialColorMedia() {
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const mediaColor = activeMedia?.dataset.bpdMediaColor;

        if (mediaColor) this.applyColorScope(mediaColor);
      }

      applyColorScope(mediaColor) {
        if (!mediaColor) return;

        const scopeItems = (container, itemSelector) => {
          if (!container) return;

          Array.from(container.children)
            .filter((item) => item.matches(itemSelector))
            .forEach((item) => {
              const itemColor = item.dataset.bpdMediaColor;
              const shouldHide = itemColor && itemColor !== mediaColor;
              item.hidden = shouldHide;

              if (shouldHide) {
                item.classList.remove('is-active');
                item.querySelector('button[aria-current]')?.removeAttribute('aria-current');
              }
            });
        };

        scopeItems(this.elements.viewer?.slider, '[data-media-id]', mediaColor);
        scopeItems(this.elements.thumbnails?.slider, '[data-target]', mediaColor);
        this.dataset.bpdSelectedMediaColor = mediaColor;
      }

      sortMediaByColor(mediaColor) {
        this.applyColorScope(mediaColor);

        if (this.elements.viewer.slider) {
          this.sortMediaItems(this.elements.viewer.slider, '[data-media-id]', mediaColor);
          this.elements.viewer.resetPages();
        }

        if (this.elements.thumbnails?.slider) {
          this.sortMediaItems(this.elements.thumbnails.slider, '[data-target]', mediaColor);
          this.elements.thumbnails.slider.scrollTo({ left: 0 });
          this.elements.thumbnails.resetPages();
        }

        this.updateBpdProgress();
        this.updateBpdArrowState();
        this.updateBpdActiveMediaRatio();
        this.updateBpdMobileDots();
        this.preloadBpdGalleryMedia();
      }

      sortMediaItems(container, itemSelector, mediaColor) {
        const items = Array.from(container.children).filter((item) => item.matches(itemSelector));

        items
          .map((item, index) => ({
            item,
            index,
            isSelectedColor: item.dataset.bpdMediaColor === mediaColor,
            order: Number.parseInt(item.dataset.bpdMediaOrder || index + 1, 10),
          }))
          .sort((a, b) => {
            if (a.isSelectedColor !== b.isSelectedColor) return a.isSelectedColor ? -1 : 1;
            return a.order - b.order || a.index - b.index;
          })
          .forEach(({ item }) => container.append(item));
      }

      setActiveMedia(mediaId, prepend, shouldScroll = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not([hidden])') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement?.firstChild !== activeThumbnail &&
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (this.elements.viewer.slider) this.elements.viewer.resetPages();

        this.setBpdPendingProgrammaticScroll(activeMedia);
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!shouldScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.preloadBpdGalleryMedia(activeMedia);
        this.updateBpdProgress();
        this.updateBpdArrowState();
        this.updateBpdActiveMediaRatio(activeMedia);
        this.updateBpdMobileDots(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.updateBpdMobileDots(activeMedia);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setBpdPendingProgrammaticScroll(activeMedia) {
        if (this.mql.matches || !activeMedia || !this.elements.viewer?.slider) return;

        this.bpdPendingScrollMediaId = activeMedia.dataset.mediaId || null;
        this.bpdPendingScrollTargetLeft = activeMedia.offsetLeft || 0;
        this.bpdPendingScrollUntil = window.performance.now() + 1400;
      }

      clearBpdPendingProgrammaticScroll() {
        this.bpdPendingScrollMediaId = null;
        this.bpdPendingScrollTargetLeft = 0;
        this.bpdPendingScrollUntil = 0;
      }

      getVisibleBpdThumbnailItems() {
        if (!this.elements.thumbnails?.slider) return [];

        return Array.from(this.elements.thumbnails.slider.children).filter((item) => {
          return item.matches('[data-target]') && !item.hidden;
        });
      }

      updateBpdMobileDots(currentElement = null) {
        if (!this.elements.thumbnails?.slider) return;

        const maxDots = 6;
        const activeSlotTarget = maxDots - 2;
        const thumbnailTrack = this.elements.thumbnails.slider;
        const thumbnailItems = Array.from(thumbnailTrack.children).filter((item) =>
          item.matches('[data-target]')
        );
        const resetItem = (item) => {
          const button = item.querySelector('button');

          item.classList.remove(
            'bpd-mobile-dot-before-window',
            'bpd-mobile-dot-after-window',
            'bpd-mobile-dot-small'
          );
          item.removeAttribute('aria-hidden');
          button?.removeAttribute('tabindex');
        };

        const resetWindow = () => {
          thumbnailItems.forEach(resetItem);
          this.elements.thumbnails.classList.remove('bpd-mobile-dots-windowed');
          thumbnailTrack.classList.remove('bpd-mobile-dots-windowed');
          thumbnailTrack.style.removeProperty('--bpd-mobile-dots-start');
          this.bpdMobileDotsStartIndex = 0;
        };

        if (this.mql.matches) {
          resetWindow();
          return;
        }

        const visibleItems = this.getVisibleBpdThumbnailItems();
        if (visibleItems.length <= maxDots) {
          resetWindow();
          return;
        }

        const activeMediaId =
          currentElement?.dataset?.mediaId ||
          currentElement?.dataset?.target ||
          visibleItems.find((item) => item.querySelector('button[aria-current]'))?.dataset.target ||
          this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        const activeThumbnail =
          (activeMediaId && visibleItems.find((item) => item.dataset.target === activeMediaId)) ||
          visibleItems[0];
        const activeIndex = Math.max(visibleItems.indexOf(activeThumbnail), 0);
        const maxStart = Math.max(visibleItems.length - maxDots, 0);
        let startIndex = Number.isFinite(this.bpdMobileDotsStartIndex) ? this.bpdMobileDotsStartIndex : 0;
        startIndex = Math.min(Math.max(startIndex, 0), maxStart);

        if (activeIndex > startIndex + activeSlotTarget) {
          startIndex = activeIndex - activeSlotTarget;
        } else if (activeIndex <= startIndex) {
          startIndex = activeIndex - 1;
        }

        startIndex = Math.min(Math.max(startIndex, 0), maxStart);
        this.bpdMobileDotsStartIndex = startIndex;
        const endIndex = startIndex + maxDots - 1;

        this.elements.thumbnails.classList.add('bpd-mobile-dots-windowed');
        thumbnailTrack.classList.add('bpd-mobile-dots-windowed');
        const nextStart = String(startIndex);
        if (thumbnailTrack.style.getPropertyValue('--bpd-mobile-dots-start') !== nextStart) {
          thumbnailTrack.style.setProperty('--bpd-mobile-dots-start', nextStart);
        }

        thumbnailItems.forEach((item) => {
          const index = visibleItems.indexOf(item);
          if (index < 0) {
            resetItem(item);
            return;
          }

          const isBeforeWindow = index < startIndex;
          const isAfterWindow = index > endIndex;
          const isLeadingContinuation = index === startIndex && startIndex > 0;
          const isTrailingContinuation = index === endIndex && endIndex < visibleItems.length - 1;
          const button = item.querySelector('button');

          item.classList.toggle('bpd-mobile-dot-before-window', isBeforeWindow);
          item.classList.toggle('bpd-mobile-dot-after-window', isAfterWindow);
          item.classList.toggle('bpd-mobile-dot-small', isLeadingContinuation || isTrailingContinuation);

          if (isBeforeWindow || isAfterWindow) {
            item.setAttribute('aria-hidden', 'true');
            button?.setAttribute('tabindex', '-1');
          } else {
            item.removeAttribute('aria-hidden');
            button?.removeAttribute('tabindex');
          }
        });
      }

      getVisibleColorMediaItems() {
        if (!this.elements.viewer?.slider) return [];

        return Array.from(this.elements.viewer.slider.children).filter((item) => {
          return item.matches('[data-media-id]') && !item.hidden;
        });
      }

      goToBpdRelativeMedia(direction) {
        const visibleItems = this.getVisibleColorMediaItems();
        const activeItem = visibleItems.find((item) => item.classList.contains('is-active')) || visibleItems[0];
        const activeIndex = Math.max(visibleItems.indexOf(activeItem), 0);
        const targetIndex = Math.min(Math.max(activeIndex + direction, 0), visibleItems.length - 1);
        const targetItem = visibleItems[targetIndex];

        if (!targetItem || targetItem === activeItem) return;
        this.setActiveMedia(targetItem.dataset.mediaId, false, false);
      }

      onBpdArrowClick(event) {
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button || !this.elements.arrowControls?.contains(button)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const direction = button.name === 'next' ? 1 : -1;
        this.goToBpdRelativeMedia(direction);
      }

      isBpdTouchCapable() {
        return navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
      }

      isBpdTouchPointer(event) {
        return event.pointerType === 'touch' || event.pointerType === 'pen';
      }

      shouldIgnoreBpdSwipeTarget(target) {
        if (!(target instanceof Element)) return true;

        if (
          target.closest(
            'a, input, select, textarea, label, summary, [contenteditable="true"], .bpd-gallery-arrows, .thumbnail-slider, .product__media-icon, .deferred-media__poster, video, iframe, model-viewer'
          )
        ) {
          return true;
        }

        const button = target.closest('button');
        return Boolean(button && !button.classList.contains('product__media-toggle'));
      }

      onBpdPointerDown(event) {
        if (!this.mql.matches || !this.isBpdTouchPointer(event) || event.isPrimary === false) return;
        if (this.shouldIgnoreBpdSwipeTarget(event.target)) return;
        if (this.getVisibleColorMediaItems().length < 2) return;

        this.clearBpdPendingProgrammaticScroll();
        this.bpdTouchSwipe = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          isHorizontal: false,
          isVertical: false,
        };
      }

      onBpdPointerMove(event) {
        const swipe = this.bpdTouchSwipe;
        if (!swipe || swipe.pointerId !== event.pointerId || !this.isBpdTouchPointer(event)) return;

        const deltaX = event.clientX - swipe.startX;
        const deltaY = event.clientY - swipe.startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (swipe.isVertical) return;

        if (!swipe.isHorizontal) {
          if (absY > 10 && absY > absX * 1.25) {
            swipe.isVertical = true;
            return;
          }

          if (absX < 12 || absX <= absY * 1.25) return;

          swipe.isHorizontal = true;
          try {
            this.elements.viewer.setPointerCapture?.(event.pointerId);
          } catch {
            // Pointer capture can fail if the browser has already ended the pointer stream.
          }
        }

        event.preventDefault();
      }

      onBpdPointerUp(event) {
        const swipe = this.bpdTouchSwipe;
        if (!swipe || swipe.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - swipe.startX;
        const deltaY = event.clientY - swipe.startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const shouldSuppressClick = swipe.isHorizontal;
        const shouldNavigate = swipe.isHorizontal && absX >= 50 && absX > absY * 1.25;

        this.resetBpdTouchSwipe(event);

        if (shouldSuppressClick) this.suppressNextBpdClick();
        if (!shouldNavigate) return;

        this.goToBpdRelativeMedia(deltaX < 0 ? 1 : -1);
      }

      suppressNextBpdClick() {
        this.bpdSuppressClick = true;
        if (this.bpdSuppressClickTimeout) window.clearTimeout(this.bpdSuppressClickTimeout);
        this.bpdSuppressClickTimeout = window.setTimeout(() => {
          this.bpdSuppressClick = false;
          this.bpdSuppressClickTimeout = null;
        }, 500);
      }

      onBpdPointerCancel(event) {
        this.resetBpdTouchSwipe(event);
      }

      resetBpdTouchSwipe(event = null) {
        if (event && this.bpdTouchSwipe?.pointerId !== event.pointerId) return;

        if (event && this.elements.viewer.hasPointerCapture?.(event.pointerId)) {
          try {
            this.elements.viewer.releasePointerCapture?.(event.pointerId);
          } catch {
            // The pointer may already be released after cancel/up in WebKit.
          }
        }

        this.bpdTouchSwipe = null;
      }

      onBpdViewerClick(event) {
        if (!this.bpdSuppressClick) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.bpdSuppressClick = false;

        if (this.bpdSuppressClickTimeout) {
          window.clearTimeout(this.bpdSuppressClickTimeout);
          this.bpdSuppressClickTimeout = null;
        }
      }

      onBpdGalleryScroll() {
        this.debouncedPreloadBpdGalleryMedia();

        if (this.mql.matches || this.bpdProgressAnimationFrame) return;

        this.bpdProgressAnimationFrame = window.requestAnimationFrame(() => {
          this.bpdProgressAnimationFrame = null;
          this.updateBpdProgressFromScroll();
          this.updateBpdMobileDotsFromScroll();
        });
      }

      scheduleBpdRestoredScrollSync() {
        if (!this.elements.viewer?.slider) return;
        if (this.mql.matches) {
          this.preloadBpdGalleryMedia();
          return;
        }

        if (this.bpdRestoreSyncAnimationFrame) {
          window.cancelAnimationFrame(this.bpdRestoreSyncAnimationFrame);
          this.bpdRestoreSyncAnimationFrame = null;
        }

        this.bpdRestoreSyncTimeouts.forEach((timeout) => window.clearTimeout(timeout));
        this.bpdRestoreSyncTimeouts = [];

        this.bpdRestoreSyncAnimationFrame = window.requestAnimationFrame(() => {
          this.bpdRestoreSyncAnimationFrame = null;
          this.syncBpdMobileStateFromScroll();
        });

        [80, 250, 600, 1200].forEach((delay) => {
          const timeout = window.setTimeout(() => {
            this.bpdRestoreSyncTimeouts = this.bpdRestoreSyncTimeouts.filter((item) => item !== timeout);
            this.syncBpdMobileStateFromScroll();
          }, delay);
          this.bpdRestoreSyncTimeouts.push(timeout);
        });
      }

      syncBpdMobileStateFromScroll() {
        if (this.mql.matches) return;

        this.updateBpdProgressFromScroll();
        this.updateBpdMobileDotsFromScroll();
        this.updateBpdArrowState();
        this.preloadBpdGalleryMedia();
      }

      preloadBpdGalleryMedia(currentElement = null) {
        const visibleItems = this.getVisibleColorMediaItems();
        this.cancelBpdNextMediaWarmup();
        if (!visibleItems.length) return;

        const activeItem =
          (currentElement instanceof Element && visibleItems.includes(currentElement) ? currentElement : null) ||
          visibleItems.find((item) => item.classList.contains('is-active')) ||
          visibleItems[0];
        const activeIndex = Math.max(visibleItems.indexOf(activeItem), 0);

        visibleItems.forEach((item) => {
          const image = item.querySelector('.product__modal-opener--image img');
          if (!image) return;

          image.fetchPriority = item === activeItem ? 'high' : 'auto';
          if (item === activeItem) {
            image.loading = 'eager';
            image.decoding = 'async';
          }
        });

        if (
          document.visibilityState !== 'visible' ||
          navigator.connection?.saveData ||
          (this.mql.matches && document.readyState !== 'complete')
        ) {
          return;
        }

        const nextImage = visibleItems[activeIndex + 1]?.querySelector('.product__modal-opener--image img');
        if (!nextImage) return;

        const warmNextImage = () => {
          this.bpdPreloadIdleCallback = null;
          if (!nextImage.isConnected || document.visibilityState !== 'visible') return;
          nextImage.fetchPriority = 'low';
          nextImage.decoding = 'async';
          nextImage.loading = 'eager';
          if (typeof nextImage.decode === 'function') nextImage.decode().catch(() => {});
        };

        this.bpdPreloadIdleCallback = window.requestIdleCallback
          ? window.requestIdleCallback(warmNextImage, { timeout: 800 })
          : window.setTimeout(warmNextImage, 200);
      }

      cancelBpdNextMediaWarmup() {
        if (this.bpdPreloadIdleCallback === null) return;

        if (window.cancelIdleCallback) {
          window.cancelIdleCallback(this.bpdPreloadIdleCallback);
        } else {
          window.clearTimeout(this.bpdPreloadIdleCallback);
        }
        this.bpdPreloadIdleCallback = null;
      }

      updateBpdArrowState() {
        if (!this.elements.arrowControls) return;

        const visibleItems = this.getVisibleColorMediaItems();
        const activeItem = visibleItems.find((item) => item.classList.contains('is-active')) || visibleItems[0];
        const activeIndex = Math.max(visibleItems.indexOf(activeItem), 0);
        const previousButton = this.elements.arrowControls.querySelector('button[name="previous"]');
        const nextButton = this.elements.arrowControls.querySelector('button[name="next"]');

        previousButton?.toggleAttribute('disabled', visibleItems.length < 2 || activeIndex <= 0);
        nextButton?.toggleAttribute('disabled', visibleItems.length < 2 || activeIndex >= visibleItems.length - 1);
      }

      updateBpdActiveMediaRatio(currentElement = null) {
        const activeMedia =
          (currentElement instanceof Element ? currentElement : null) ||
          this.elements.viewer?.querySelector('[data-media-id].is-active') ||
          this.elements.viewer?.querySelector('[data-media-id]:not([hidden])');
        const mediaContainer = activeMedia?.querySelector('.product-media-container');
        const ratio = Number.parseFloat(
          mediaContainer?.style.getPropertyValue('--preview-ratio') ||
            mediaContainer?.style.getPropertyValue('--ratio') ||
            '1'
        );

        this.style.setProperty('--bpd-active-media-ratio', String(Number.isFinite(ratio) && ratio > 0 ? ratio : 1));
      }

      updateBpdProgress(currentElement = null) {
        if (!this.elements.progressBar) return;

        const visibleItems = this.getVisibleColorMediaItems();
        const activeItem =
          currentElement ||
          visibleItems.find((item) => item.classList.contains('is-active')) ||
          visibleItems[0];

        const activeIndex = Math.max(visibleItems.indexOf(activeItem), 0);
        const total = visibleItems.length || 1;
        const segmentWidth = visibleItems.length ? `${100 / total}%` : '0%';
        const segmentIndex = visibleItems.length ? activeIndex : 0;

        this.elements.progressBar.style.setProperty('--bpd-gallery-progress-size', segmentWidth);
        this.elements.progressBar.style.setProperty('--bpd-gallery-progress-index', String(segmentIndex));
        this.elements.progress?.setAttribute('aria-valuenow', String(activeIndex + 1));
        this.elements.progress?.setAttribute('aria-valuemax', String(total));
      }

      updateBpdProgressFromScroll() {
        const visibleItems = this.getVisibleColorMediaItems();
        const slider = this.elements.viewer?.slider;
        const slideWidth = visibleItems[0]?.clientWidth || slider?.clientWidth;

        if (!this.elements.progressBar || !slider || !visibleItems.length || !slideWidth) return;

        const total = visibleItems.length;
        const rawIndex = slider.scrollLeft / slideWidth;
        const activeIndex = Math.min(Math.max(rawIndex, 0), total - 1);

        this.elements.progressBar.style.setProperty('--bpd-gallery-progress-size', `${100 / total}%`);
        this.elements.progressBar.style.setProperty('--bpd-gallery-progress-index', String(activeIndex));
        this.elements.progress?.setAttribute('aria-valuenow', String(Math.round(activeIndex) + 1));
        this.elements.progress?.setAttribute('aria-valuemax', String(total));
      }

      getBpdScrollMediaItem() {
        const visibleItems = this.getVisibleColorMediaItems();
        const slider = this.elements.viewer?.slider;
        const slideWidth = visibleItems[0]?.clientWidth || slider?.clientWidth;

        if (!slider || !visibleItems.length || !slideWidth) return null;

        const scrollIndex = Math.round(slider.scrollLeft / slideWidth);
        const activeIndex = Math.min(Math.max(scrollIndex, 0), visibleItems.length - 1);

        return visibleItems[activeIndex] || null;
      }

      updateBpdMobileDotsFromScroll() {
        if (this.mql.matches || !this.elements.thumbnails) return;

        if (this.bpdPendingScrollMediaId) {
          const targetMedia = this.elements.viewer.querySelector(`[data-media-id="${this.bpdPendingScrollMediaId}"]`);
          const targetLeft = targetMedia?.offsetLeft ?? this.bpdPendingScrollTargetLeft;
          const distanceToTarget = Math.abs((this.elements.viewer.slider?.scrollLeft || 0) - targetLeft);
          const timedOut = window.performance.now() > this.bpdPendingScrollUntil;

          if (targetMedia && !timedOut && distanceToTarget > 2) {
            return;
          }

          this.clearBpdPendingProgrammaticScroll();
        }

        const activeMedia = this.getBpdScrollMediaItem();
        const mediaId = activeMedia?.dataset.mediaId;
        if (!activeMedia || !mediaId) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        activeThumbnail.querySelector('button')?.setAttribute('aria-current', true);
        this.updateBpdMobileDots(activeMedia);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!this.mql.matches) {
          this.updateBpdMobileDots(thumbnail);
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
